import React, { useState } from "react";
import { Button, Space } from "antd";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import OrderPlanModal from "./OrderPlanModal";
import AnalysisHoursModal from "./AnalysisHoursModal";
import { RootState, ApprovedHeaderProps } from "../types";

// Constants
const MODAL_TYPES = {
  PLAN: "plan",
  ANALYSIS: "analysis",
} as const;

type ModalType = (typeof MODAL_TYPES)[keyof typeof MODAL_TYPES] | null;

// Utility function to determine branch ID
const getBranchId = (
  isAdmin: boolean,
  activeBranch: number | null,
  branchId?: string
): number | string | undefined =>
  isAdmin ? (activeBranch as number) : branchId;

interface RouteParams {
  branchId?: string;
}

/**
 * ApprovedHeader Component
 *
 * This component renders action buttons in an employee salary view header that allow
 * users to open different modals related to employee working hours.
 *
 * Features:
 * - Provides buttons to show analysis of worked hours or plan details
 * - Opens appropriate modals with employee data when buttons are clicked
 * - Handles different user roles (admin vs regular user) for branch identification
 *
 * The component renders two buttons:
 * 1. Show Analysis - Opens the AnalysisHoursModal that displays detailed breakdown of employee hours
 * 2. Show Plan - Opens the OrderPlanModal that displays the employee's work plan
 *
 * @example
 * ```tsx
 * <ApprovedHeader record={employeeRecord} />
 * ```
 *
 * @param {Object} props - Component props
 * @param {EmployeeRecord} props.record - Employee data record containing details needed for modals
 */
const ApprovedHeader: React.FC<ApprovedHeaderProps> = ({ record }) => {
  const intl = useIntl();
  const { branchId } = useParams<RouteParams>();
  const location = useLocation();
  const [visibleModal, setVisibleModal] = useState<ModalType>(null);

  // Redux state
  const { activeYear, activeMonth, activeBranch } = useSelector(
    (state: RootState) => state.salary
  );
  const isAdmin = location.pathname.includes("admin");

  // Handlers
  const showModal = (modalType: ModalType) => () => setVisibleModal(modalType);
  const hideModal = () => setVisibleModal(null);

  // Styles
  const styles = {
    container: { width: "100%", marginBottom: "10px" },
    buttonContainer: {
      display: "flex",
      width: "100%",
      justifyContent: "flex-end",
    },
  };

  // Shared modal props
  const modalProps = {
    employee: record,
    activeYear,
    activeMonth,
    onHide: hideModal,
  };

  return (
    <div style={styles.container}>
      <div style={styles.buttonContainer}>
        <Space>
          <Button onClick={showModal(MODAL_TYPES.ANALYSIS)} size="middle">
            {intl.formatMessage({ id: "app.salary.show_analysis" })}
          </Button>
          <Button onClick={showModal(MODAL_TYPES.PLAN)} size="middle">
            {intl.formatMessage({ id: "app.salary.show_plan" })}
          </Button>
        </Space>
      </div>

      <OrderPlanModal
        {...modalProps}
        visible={visibleModal === MODAL_TYPES.PLAN}
      />
      <AnalysisHoursModal
        {...modalProps}
        visible={visibleModal === MODAL_TYPES.ANALYSIS}
        branch_id={getBranchId(isAdmin, activeBranch, branchId)}
      />
    </div>
  );
};

export default ApprovedHeader;
